import { useCallback } from "react";
import { t } from "@/i18n-js/instance";
import type { AnalyticsTableColumn } from "@/react/components/Analytics/components/AnalyticsTableComponent";
import type {
  BarLineChartCSVData,
  ChartDataItem,
  MappedBreakdownItem,
  PieChartCSVData,
  TableChartCSVData,
} from "@/react/components/Analytics/components/types";
import type { ReferralUriTableData } from "@/react/components/SettingsApp/AnalyticsV4/mappers/paywalls/conversion-mappers";
// Import specific table data types
import type { PaywallTableData } from "@/react/components/SettingsApp/AnalyticsV4/mappers/paywalls/overview-mappers";
import { useToast } from "@circle-react-uikit/ToastV2";
import { CHART_TYPES, type ChartType } from "./chartTypes";

type TableData = PaywallTableData | ReferralUriTableData;
interface CSVDownloadOptions {
  columns?: AnalyticsTableColumn<any>[];
  filename?: string;
  onError?: () => void;
  onSuccess?: () => void;
  seriesKeys?: string[];
}

const rootLocale = "settings.analytics.export_csv";

export const useAnalyticsCSVDownload = () => {
  const { success, error } = useToast();

  const downloadCSV = useCallback(
    (
      data: ChartDataItem[] | MappedBreakdownItem[] | TableData[],
      chartType: ChartType,
      title: string,
      options: CSVDownloadOptions & { prefix?: string; suffix?: string } = {},
    ) => {
      try {
        if (data.length === 0) return;

        const {
          filename = `${title}.csv`,
          onSuccess,
          prefix = "",
          suffix = "",
          seriesKeys,
          columns,
        } = options;

        const csvData = getCSVDataForChartType(data, chartType, {
          seriesKeys,
          columns,
          prefix,
          suffix,
        });

        const csvContent = generateCSVContent(csvData);

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");

        // eslint-disable-next-line sonarjs/different-types-comparison -- Disabled for plugin version update. Fix when possible.
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", filename);
          link.style.visibility = "hidden";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }

        success(t([rootLocale, "success_message"], { report_name: filename }));
        onSuccess?.();
      } catch (err) {
        console.error("CSV download error:", err);
        error(t([rootLocale, "error_message"]));
        options.onError?.();
      }
    },
    [success, error],
  );

  return {
    downloadCSV,
  };
};

const getCSVDataForChartType = (
  data: ChartDataItem[] | MappedBreakdownItem[] | TableData[],
  chartType: ChartType,
  options: {
    seriesKeys?: string[];
    columns?: AnalyticsTableColumn<any>[];
    prefix: string;
    suffix: string;
  },
): PieChartCSVData[] | BarLineChartCSVData[] | TableChartCSVData[] => {
  switch (chartType) {
    case CHART_TYPES.PIE:
      return getPieChartCSVData(data as MappedBreakdownItem[], options);

    case CHART_TYPES.BAR:
    case CHART_TYPES.LINE:
      return getBarLineChartCSVData(data as ChartDataItem[], options);

    case CHART_TYPES.TABLE:
    case CHART_TYPES.CHOROPLETH:
      return getTableChartCSVData(data as TableData[], options);

    default:
      return data as
        | PieChartCSVData[]
        | BarLineChartCSVData[]
        | TableChartCSVData[];
  }
};

const getPieChartCSVData = (
  data: MappedBreakdownItem[],
  options: {
    prefix: string;
    suffix: string;
  },
): PieChartCSVData[] => {
  const { prefix, suffix } = options;

  return data.map(item => ({
    Category: item.label,
    Value: `${prefix}${item.value.toLocaleString()}${suffix}`,
  }));
};

const getBarLineChartCSVData = (
  data: ChartDataItem[],
  options: {
    seriesKeys?: string[];
    prefix: string;
    suffix: string;
  },
): BarLineChartCSVData[] => {
  const { seriesKeys = [], prefix, suffix } = options;

  return data.map(item => {
    const csvRow: BarLineChartCSVData = {
      Date: item.label,
    };

    seriesKeys.forEach(key => {
      const value = item[key];
      if (typeof value === "number") {
        csvRow[key] = `${prefix}${value.toLocaleString()}${suffix}`;
      } else {
        csvRow[key] =
          typeof value === "object" ? JSON.stringify(value) : String(value);
      }
    });

    return csvRow;
  });
};

const formatTableValue = (
  value: unknown,
  column: AnalyticsTableColumn<any>,
  prefix: string,
  suffix: string,
): string | number => {
  if (typeof value === "number") {
    const colPrefix = column.prefix || prefix;
    const colSuffix = column.suffix || suffix;
    return `${colPrefix}${value.toLocaleString()}${colSuffix}`;
  }

  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const getTableChartCSVData = (
  data: TableData[],
  options: {
    columns?: AnalyticsTableColumn<any>[];
    prefix: string;
    suffix: string;
  },
): TableChartCSVData[] => {
  const { columns, prefix, suffix } = options;

  if (!columns || columns.length === 0) {
    return data as TableChartCSVData[];
  }

  return data.map(item => {
    const csvRow: TableChartCSVData = {};

    columns.forEach(column => {
      const value = item[column.id];

      if (value !== undefined && value !== null) {
        csvRow[column.header] = formatTableValue(value, column, prefix, suffix);
      }
    });

    return csvRow;
  });
};

const generateCSVContent = (data: any[]): string => {
  if (!data || data.length === 0) {
    return "No data available";
  }
  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(","),
    ...data.map(row =>
      headers
        .map(header => {
          const value = row[header];
          if (
            typeof value === "string" &&
            (value.includes(",") || value.includes('"') || value.includes("\n"))
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(","),
    ),
  ];

  return csvRows.join("\n");
};
